import { useNavigate, useLocation } from '../routing';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { documentsApi, dishesApi } from '../utils/api';
import UserMenu from './UserMenu';
import TenantChip from './TenantChip';
import Icon from './Icon';

// Customer-facing links — visible to everyone (admin + viewer).
const CUSTOMER_LINKS = [
  { path: '/suppliers', label: 'ספקים',    icon: 'suppliers' },
  { path: '/credits',   label: 'זיכויים',   icon: 'credits' },
  { path: '/analysis',  label: 'ניתוח רכש', icon: 'analysis' },
  // NOTE: neither /item-classifications (סיווג פריטים) nor /budget (קטגוריות) is a
  // nav entry, and for the same reason: neither is a destination. You go there
  // because a number on ניתוח רכש prompted it — "לא מסווג" for tagging,
  // "לא משויך לאף קטגוריה" or a missing budget for budgeting. Both are
  // reached from the card that raised the question
  // (components/ClassificationAnalysis.jsx, components/WeeklyBudgetBreakdown.jsx),
  // which is also what keeps the top bar to the five screens people open on
  // purpose.
  { path: '/documents', label: 'מסמכים',   icon: 'documents' },
  { path: '/dishes',    label: 'עץ מוצר',  icon: 'tree', badgeKey: 'dishAlerts' },
];

// System administration moved to the Peak Command Center, reached from the
// account menu — one door, not two. What stays here is the day-to-day admin
// work that IS tenant-scoped and belongs beside the customer screens.
// All remaining admin destinations live under a single "ניהול" menu (dropdown on desktop,
// labeled drawer subsection on mobile). טיוטות carries the pending-drafts badge,
// which also bubbles up to the closed menu trigger so admins still feel the
// pressure without opening it.
const ADMIN_LINKS = [
  { path: '/upload',           label: 'העלאת מסמך' },
  { path: '/drafts',           label: 'טיוטות', badgeKey: 'drafts' },
  { path: '/manage-suppliers', label: 'ניהול ספקים' },
  { path: '/unpriced-items',   label: 'פריטים ללא מחיר' },
  { path: '/settings',         label: 'הגדרות' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { restaurantId, canEdit, isAdmin } = useAuth();
  const { dishCostingEnabled } = useSettings();
  // Both gates, mirroring the server's requireDishModule + requireWrite. Used
  // for the link AND for the badge request — see below.
  const dishModuleVisible = dishCostingEnabled && canEdit;
  const customerLinks = dishModuleVisible
    ? CUSTOMER_LINKS
    : CUSTOMER_LINKS.filter(l => l.path !== '/dishes');
  const [drawerOpen, setDrawerOpen]         = useState(false);
  const [adminMenuOpen, setAdminMenuOpen]   = useState(false);
  const [draftsCount, setDraftsCount]       = useState(0);
  const [dishAlertCount, setDishAlertCount] = useState(0);

  // Pending-drafts badge. Refetched on route change AND on focus / visibility
  // change, so uploading a doc in another tab (which lands as טיוטה) updates
  // the badge without a hard reload.
  const reloadDraftsCount = useCallback(() => {
    if (!isAdmin) return;
    documentsApi.getDraftsCount()
      .then(r => setDraftsCount(r.data?.count || 0))
      .catch(() => {});
  }, [isAdmin]);

  useEffect(() => { reloadDraftsCount(); }, [reloadDraftsCount, location.pathname, restaurantId]);

  // Dish price-change alerts badge. Skipped entirely unless the module is
  // visible to this user: before this gate the request went out for EVERY user
  // on EVERY route change, including restaurants with no dishes at all — and
  // now that the server gates /api/dishes, it would 403 every time.
  const reloadDishAlerts = useCallback(() => {
    if (!dishModuleVisible) { setDishAlertCount(0); return; }
    dishesApi.getAlertsCount()
      .then(r => setDishAlertCount(r.data?.count || 0))
      .catch(() => {});
  }, [dishModuleVisible]);
  // Deliberately NOT keyed on location.pathname, unlike the drafts badge above.
  // This count is expensive to produce — it resolves the current price of every
  // product used in a dish — and it answers a question that only changes when a
  // DOCUMENT is ingested, never when someone clicks a nav link. Recomputing it
  // per navigation put that work on the critical path of every page in the app,
  // synchronously, on the one Node process. Mount, tenant switch, and the
  // explicit acknowledge event below are the three moments it can actually
  // change for the viewer; a newly ingested price shows up on their next load.
  useEffect(() => { reloadDishAlerts(); }, [reloadDishAlerts, restaurantId]);
  // Refresh the badge immediately when alerts are acknowledged on /dishes
  // (which is a same-route action, so nothing else would re-fire it).
  useEffect(() => {
    window.addEventListener('dish-alerts-changed', reloadDishAlerts);
    return () => window.removeEventListener('dish-alerts-changed', reloadDishAlerts);
  }, [reloadDishAlerts]);

  // Auto-close all menus on route change.
  useEffect(() => {
    setDrawerOpen(false);
    setAdminMenuOpen(false);
  }, [location.pathname]);

  // Escape closes the mobile drawer and the admin dropdown (parity with the
  // other menus, which the TenantChip already had).
  useEffect(() => {
    if (!drawerOpen && !adminMenuOpen) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setDrawerOpen(false);
      setAdminMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen, adminMenuOpen]);

  useEffect(() => {
    if (!isAdmin) return;
    const onFocus = () => { if (!document.hidden) reloadDraftsCount(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [isAdmin, reloadDraftsCount]);

  const badgeFor = (key) => key === 'drafts' ? draftsCount : key === 'dishAlerts' ? dishAlertCount : 0;

  const renderNavLink = (l) => (
    <button
      key={l.path}
      className={`nav-link${location.pathname === l.path ? ' active' : ''}`}
      onClick={() => navigate(l.path)}
    >
      {l.icon && <Icon name={l.icon} />}
      <span>{l.label}</span>
      {l.badgeKey && badgeFor(l.badgeKey) > 0 && (
        <span
          className="admin-trigger-badge"
          aria-label={l.badgeKey === 'drafts' ? `${badgeFor(l.badgeKey)} טיוטות ממתינות`
            : l.badgeKey === 'dishAlerts' ? `${badgeFor(l.badgeKey)} מנות עם שינוי מחיר` : undefined}
        >{badgeFor(l.badgeKey)}</span>
      )}
    </button>
  );

  // The bar carries a deeper shadow ONLY while the page is scrolled. At rest it
  // should sit quietly on the page; the lift is information — it says content
  // is passing underneath, which is exactly when a sticky surface needs to look
  // like one. Passive listener + a boolean flip, so the handler does no layout
  // work and only re-renders on the single frame the state actually changes.
  const [scrolled, setScrolled] = useState(() => typeof window !== 'undefined' && window.scrollY > 0);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const adminActive = ADMIN_LINKS.some(l => l.path === location.pathname);

  return (
    <nav className={`navbar${scrolled ? ' is-scrolled' : ''}`}>
      {/* The bar's background is full-bleed; this wrapper puts its CONTENT on
          the same column as the page, so the wordmark lands exactly above the
          page title and the card edge at every width. See `.navbar-inner`. */}
      <div className="navbar-inner">
      {/* Right cluster (RTL start): brand, active-restaurant context, then nav.
          The restaurant switcher lives HERE — a high-frequency work-context
          control — deliberately separated from the account avatar (far end). */}
      <div className="navbar-left">
        <button
          className="navbar-hamburger"
          aria-label={drawerOpen
            ? 'סגור תפריט'
            : (isAdmin && draftsCount > 0 ? `פתח תפריט — ${draftsCount} טיוטות ממתינות` : 'פתח תפריט')}
          aria-expanded={drawerOpen}
          onClick={() => setDrawerOpen(o => !o)}
        >
          <Icon name={drawerOpen ? 'close' : 'menu'} size={22} strokeWidth={2} />
          {/* Drafts pressure moves into the drawer on mobile; a dot keeps it
              visible on the collapsed hamburger. */}
          {!drawerOpen && isAdmin && draftsCount > 0 && (
            <span className="navbar-hamburger-dot" aria-hidden="true" />
          )}
        </button>

        <button
          type="button"
          className="navbar-brand"
          onClick={() => navigate('/')}
          aria-label="Peak — דף הבית"
        >
          PEAK
        </button>

        <span className="navbar-tenant"><TenantChip /></span>
        <span className="navbar-divider" aria-hidden="true" />

        {/* Outside-tap dismissal for the open mobile drawer (parity with the
            other menus). CSS renders it only while the drawer is a drawer. */}
        {drawerOpen && <div className="drawer-backdrop" onClick={() => setDrawerOpen(false)} />}

        {/* Desktop navigation. On mobile (≤900px) this collapses to the drawer
            (see CSS .navbar-nav rules under the @media block). */}
        <div className={`navbar-nav${drawerOpen ? ' open' : ''}`}>
          {customerLinks.map(renderNavLink)}

          {/* Admin grouping — DESKTOP dropdown. All admin destinations live
              here under one "ניהול" menu. Hidden ≤900px; the drawer subsection
              below takes over on mobile. */}
          {isAdmin && (
            <div className="admin-menu-wrapper">
              <button
                className={`nav-link nav-link-with-caret${adminActive ? ' active' : ''}`}
                onClick={() => setAdminMenuOpen(o => !o)}
                aria-haspopup="menu"
                aria-expanded={adminMenuOpen}
                aria-label="תפריט ניהול"
              >
                <Icon name="admin" />
                <span>ניהול</span>
                <span className="nav-link-caret"><Icon name="chevron" size={13} strokeWidth={2} /></span>
                {/* Drafts pressure bubbles to the trigger while the menu is closed. */}
                {!adminMenuOpen && draftsCount > 0 && (
                  <span className="admin-trigger-badge" aria-label={`${draftsCount} טיוטות ממתינות`}>
                    {draftsCount}
                  </span>
                )}
              </button>
              {adminMenuOpen && (
                <>
                  <div className="menu-backdrop" onClick={() => setAdminMenuOpen(false)} />
                  <div className="admin-menu-panel" role="menu">
                    {ADMIN_LINKS.map(l => (
                      <button
                        key={l.path}
                        role="menuitem"
                        className={`admin-menu-item${location.pathname === l.path ? ' active' : ''}`}
                        onClick={() => { setAdminMenuOpen(false); navigate(l.path); }}
                      >
                        <span>{l.label}</span>
                        {l.badgeKey && badgeFor(l.badgeKey) > 0 && (
                          <span className="admin-menu-badge">{badgeFor(l.badgeKey)}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Admin grouping — MOBILE. A labeled subsection inside the drawer
              holding every admin link (the dropdown has no equivalent here). */}
          {isAdmin && (
            <div className="drawer-admin-section">
              <div className="drawer-admin-title">
                <Icon name="admin" size={15} />
                <span>ניהול</span>
              </div>
              {ADMIN_LINKS.map(renderNavLink)}
            </div>
          )}
        </div>
      </div>

      <div className="navbar-user-cluster">
        <UserMenu />
      </div>
      </div>
    </nav>
  );
}
